import { GetRequest } from "@/lib/http";
import { History, Settings } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import {
  airtelLogo,
  darkIkjNepaLogo,
  dstvLogo,
  egNepaLogo,
  etisalatLogo,
  gloLogo,
  gotvLogo,
  ibNepaLogo,
  ikjNepaLogo,
  mtnLogo,
  nepaLogo,
  showmaxLogo,
  startimesLogo,
} from "@/assets";
import Notiflix, { Confirm } from "notiflix";
import { Skeleton } from "../ui/skeleton";
import { FaUsersSlash } from "react-icons/fa6";
import { useTheme } from "@/hooks/useTheme";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const BeneficiaryTiles = () => {
  const [beneficiariesData, setBeneficiariesData] = React.useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const navigate = useNavigate();

  const fetchBeneficiary = async () => {
    const { data, err } = await GetRequest({
      url: "/api/beneficiary",
      setState: setLoading,
      errorMessage: "Error fetching beneficiaries",
    });

    if (!err) {
      if (data?.some((item) => !item.name || item.name.trim() === "")) {
        setOpenDialog(true);
      }

      setBeneficiariesData(data);
    } else {
      console.log(err);
    }
  };

  const networkImages = {
    mtn: mtnLogo,
    airtel: airtelLogo,
    glo: gloLogo,
    "9mobile": etisalatLogo,
  };

  const cableImages = {
    dstv: dstvLogo,
    gotv: gotvLogo,
    startimes: startimesLogo,
    showmax: showmaxLogo,
  };

  const discoImages = {
    ikeja: useTheme().theme === "dark" ? darkIkjNepaLogo : ikjNepaLogo,
    enugu: egNepaLogo,
    ibadan: ibNepaLogo,
    eko: nepaLogo,
  };

  const navigateTo = (item) => {
    if (item.type === "phone") {
      Confirm.show(
        "Choose Service",
        "What would you like to purchase?",
        "Airtime",
        "Data",
        () => {
          navigate("/dashboard/buy-airtime", { state: item });
        },
        () => {
          navigate("/dashboard/buy-data", { state: item });
        },
        {
          titleColor: "#3b82f6",
          okButtonBackground: "#3b82f6",
          cancelButtonBackground: "#3b82f6",
        },
      );
    } else if (item.type === "meter") {
      navigate("/dashboard/buy-electricity-bills", { state: item });
    } else if (item.type === "iuc") {
      navigate("/dashboard/buy-cable-subscription", { state: item });
    }
  };
  useEffect(() => {
    fetchBeneficiary();
  }, []);

  return (
    <div>
      {loading ? (
        <div className="dark:border-darkBorder/30 mt-5 flex w-full flex-col gap-2 rounded-xl border border-gray-100 bg-gray-100/70 p-4 md:p-5 dark:bg-[#111417]">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Saved Beneficiaries</h2>

            <Settings
              className="text-primary cursor-pointer"
              onClick={() => navigate("/dashboard/beneficiaries")}
            />
          </div>

          <div className="flex w-full items-center gap-3">
            {Array(4)
              .fill()
              .map((_, index) => (
                <div
                  key={index}
                  className="mt-4 flex flex-col items-center gap-2"
                >
                  <Skeleton className="size-16 rounded-full" />

                  <Skeleton className="h-5 rounded px-10" />
                </div>
              ))}
          </div>
        </div>
      ) : beneficiariesData.length > 0 ? (
        <>
          <div className="dark:border-darkBorder/30 mt-5 flex w-full flex-col gap-2 rounded-xl border border-gray-100 bg-gray-100/70 p-4 md:p-5 dark:bg-[#111417]">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Saved Beneficiaries</h2>

              <Settings
                className="text-primary cursor-pointer"
                onClick={() => navigate("/dashboard/beneficiaries")}
              />
            </div>

            <ScrollArea className="max-w-[600px] lg:max-w-none">
              <div className="flex w-max space-x-8 pt-4 pb-3 lg:space-x-12">
                {beneficiariesData
                  .sort(
                    (a, b) => new Date(b.updated_at) - new Date(a.updated_at),
                  )
                  .map((item, index) => (
                    <div key={index} onClick={() => navigateTo(item)}>
                      <div className="group flex cursor-pointer flex-col items-center gap-2 rounded-lg">
                        <img
                          src={
                            item.type === "phone"
                              ? networkImages[item.network.toLowerCase()]
                              : item.type === "meter"
                                ? discoImages[item.disco.toLowerCase()]
                                : cableImages[item?.cable?.toLowerCase()]
                          }
                          alt="provider-logo"
                          className="dark:border-darkBorder size-14 rounded-full border transition-all duration-300 group-hover:scale-110"
                        />

                        <h3 className="group-hover:text-primary text-sm font-medium group-hover:font-bold dark:group-hover:text-white">
                          {item?.name}
                        </h3>
                      </div>
                    </div>
                  ))}
              </div>
              <ScrollBar orientation="horizontal" className="invisible" />
            </ScrollArea>
          </div>
          <div>
            <AlertDialog open={openDialog} onOpenChange={setOpenDialog}>
              <AlertDialogContent className="dark:border-darkBorder dark:bg-darkInput z-[999999] w-[100%] rounded-2xl border-gray-200 bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Beneficiary Error</AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-600 dark:text-gray-200">
                    Please add names to all your beneficiaries before
                    proceeding. This helps you better identify and manage your
                    saved contacts.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogAction
                    className="text-white"
                    onClick={() => {
                      setOpenDialog(false);
                      navigate("/dashboard/beneficiaries");
                    }}
                  >
                    Continue
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>{" "}
        </>
      ) : (
        <div className="dark:border-darkBorder/30 dark:bg-darkBg mt-5 flex flex-col items-center justify-center gap-2 rounded-xl border border-gray-100 bg-gray-100/70 py-8">
          <div className="bg-primary flex size-14 items-center justify-center rounded-full">
            <FaUsersSlash size={27} className="text-white" />
          </div>

          <h2 className="text-xl font-semibold">Update on Bills</h2>

          <p className="max-w-sm text-center text-gray-500">
            We made some changes to how bills payment works, you can now add
            beneficiaries for future purchase
          </p>

          <Link
            to="/dashboard/beneficiaries"
            className="bg-primary rounded-full px-3 py-2 text-white"
          >
            Add Beneficiaries
          </Link>
        </div>
      )}
    </div>
  );
};

export default BeneficiaryTiles;
